"use client";

import { useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { AlertCircle, RefreshCw } from "lucide-react";
import { reportClientError } from "@/lib/client-error-beacon";

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const reported = useRef(false);
  useEffect(() => {
    console.error("Global Application Error:", error);
    if (!reported.current) {
      reported.current = true;
      reportClientError(error);
    }
  }, [error]);

  const handleHardReset = () => {
    // Force a full page reload to clear stale Server Action IDs and cache
    window.location.reload();
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-background p-6 text-center">
      <div className="w-20 h-20 bg-error/10 text-error rounded-full flex items-center justify-center mb-6 animate-pulse">
        <AlertCircle size={40} />
      </div>

      <h1 className="text-4xl font-black tracking-tighter mb-2">
        Algo deu errado
      </h1>

      <p className="text-base-content/60 max-w-md mb-8 font-medium">
        Ocorreu um erro crítico no sistema. Isso pode acontecer após uma atualização.
        Tente recarregar a página para sincronizar os dados.
      </p>

      <div className="flex flex-col sm:flex-row gap-4">
        <Button
          onClick={() => reset()}
          variant="outline"
          className="h-12 px-8 rounded-lg font-bold border-base-300 hover:bg-base-200"
        >
          Tentar Novamente
        </Button>

        <Button
          onClick={handleHardReset}
          className="h-12 px-8 rounded-lg font-black bg-primary text-primary-content hover:opacity-90 shadow-lg shadow-primary/20 gap-2"
        >
          <RefreshCw size={18} />
          Recarregar Tudo (Hard Refresh)
        </Button>
      </div>

      {error.digest && (
        <p className="mt-8 text-[10px] font-mono text-base-content/20 uppercase tracking-widest">
          ID do Erro: {error.digest}
        </p>
      )}
    </div>
  );
}
